import {
  Tooltip,
  ToggleGroupItem,
  ToggleGroupRoot,
} from '@plerionhq/design-system';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import {
  applyPresetParams,
  PRESET_KEYS,
  presetViews,
  type PresetView,
} from './preset-views';

// A preset is "active" iff every PRESET_KEY in the URL matches what this preset
// would set (and unset). Filters managed by the sidebar — search, integration,
// mechanism, principalType, resource type — are intentionally ignored: presets
// are a coarse origin/scope/trust slice that users layer additional facets on
// top of. "All" matches when no preset-managed key is set, even if other facets
// are.
function isActive(preset: PresetView, params: URLSearchParams): boolean {
  const presetParams = preset.params();
  const presetEntries = Object.entries(presetParams);
  for (const key of PRESET_KEYS) {
    const current = params.getAll(key).join(',');
    const expected = (presetParams[key] ?? []).join(',');
    if (current !== expected) {
      return false;
    }
  }
  return presetEntries.length > 0 || PRESET_KEYS.every((k) => !params.has(k));
}

const getPosition = (
  index: number,
  total: number,
): 'first' | 'middle' | 'last' => {
  if (index === 0) {
    return 'first';
  }
  if (index === total - 1) {
    return 'last';
  }
  return 'middle';
};

export function PresetChips() {
  const { t } = useTranslation('accessGrants');
  const presets = useMemo(() => presetViews(t), [t]);
  const [searchParams, setSearchParams] = useSearchParams();

  const activeId = presets.find((p) => isActive(p, searchParams))?.id ?? '';

  const apply = (id: string) => {
    if (!id) {
      return;
    }
    const preset = presets.find((p) => p.id === id);
    if (!preset) {
      return;
    }
    setSearchParams(applyPresetParams(searchParams, preset.params()));
  };

  return (
    <div className="flex items-center overflow-auto rounded-md">
      <div className="bg-opacity-10 mr-2 flex items-center rounded-md py-2">
        <span className="text-br-primary text-sm font-medium whitespace-nowrap">
          {t('common:show')}
        </span>
      </div>
      <ToggleGroupRoot type="single" value={activeId} onValueChange={apply}>
        {presets.map((preset, index) => (
          <Tooltip key={preset.id} label={preset.description}>
            <ToggleGroupItem
              value={preset.id}
              position={getPosition(index, presets.length)}
              className="bg-white whitespace-nowrap dark:bg-gray-900"
              data-testid={`preset-chip-${preset.id}`}
            >
              {preset.label}
            </ToggleGroupItem>
          </Tooltip>
        ))}
      </ToggleGroupRoot>
    </div>
  );
}
